import { atom } from 'jotai';
import { Task, TaskSearchParams, CreateTaskData, UpdateTaskData } from '../types/task';
import { TaskService } from '../../services/taskService';
import { backgroundRefreshingAtom, lastDataRefreshAtom, useCacheFirstAtom } from './uiAtoms';
import { v4 as uuidv4 } from 'uuid';
import { translationEnabledAtom } from './translationAtoms';
import i18n from '../../lib/i18n';
import { logger } from '../../lib/logger';
// Import focus atoms for cleanup when tasks are updated
import {
  basePlayingTaskIdsAtom,
  ephemeralTaskSessionsAtom,
  userSetTaskProgressAtom,
  focusedTaskIdAtom,
  headerActiveDisplayAtom
} from '../v3/focusAtoms';

// 任务列表状态
export const tasksAtom = atom<Task[]>([]);

// 任务加载状态
export const tasksLoadingAtom = atom<boolean>(false);

// 任务加载错误状态
export const tasksErrorAtom = atom<string | null>(null);

// 任务过滤器状态
export const taskFilterDoneAtom = atom<boolean | null>(null);
export const taskFilterPriorityAtom = atom<string | null>(null);
export const taskSearchQueryAtom = atom<string>('');

// 派生的过滤任务状态
export const filteredTasksAtom = atom((get) => {
  const tasks = get(tasksAtom);
  const filterDone = get(taskFilterDoneAtom);
  const filterPriority = get(taskFilterPriorityAtom);
  const searchQuery = get(taskSearchQueryAtom);

  return tasks.filter((task) => {
    // 根据完成状态过滤
    if (filterDone !== null && task.done !== filterDone) {
      return false;
    }

    // 根据优先级过滤
    if (filterPriority === 'vital' && !task.vital) {
      return false;
    }
    if (filterPriority === 'urgent' && !task.urgent) {
      return false;
    }
    if (filterPriority === 'fixed' && !task.fixed) {
      return false;
    }

    // 根据搜索查询过滤
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        task.title.toLowerCase().includes(query) ||
        (task.notes && task.notes.toLowerCase().includes(query))
      );
    }

    return true;
  });
});

// 加载任务的函数
export const fetchTasksAtom = atom(
  null,
  async (get, set) => {
    // 获取相关状态
    const useCacheFirst = get(useCacheFirstAtom);
    const lastRefreshTimes = get(lastDataRefreshAtom);
    const hasCachedData = get(tasksAtom).length > 0;

    // 如果使用缓存优先且有缓存数据，则使用后台刷新模式
    const useBackgroundRefresh = useCacheFirst && hasCachedData;

    if (useBackgroundRefresh) {
      // 使用后台刷新模式
      set(backgroundRefreshingAtom, true);
      try {
        const data = await TaskService.getTasks();
        set(tasksAtom, data);
        // 更新最后刷新时间
        set(lastDataRefreshAtom, {
          ...lastRefreshTimes,
          tasks: Date.now()
        });
      } catch (err) {
        logger.error('Failed to fetch tasks in background', err instanceof Error ? err : new Error(String(err)), { atom: 'fetchTasksAtom' });
        set(tasksErrorAtom, err instanceof Error ? err.message : '未知错误');
      } finally {
        set(backgroundRefreshingAtom, false);
      }
    } else {
      // 使用普通加载模式（显示全屏加载状态）
      set(tasksLoadingAtom, true);
      set(tasksErrorAtom, null);
      try {
        const data = await TaskService.getTasks();
        set(tasksAtom, data);
        // 更新最后刷新时间
        set(lastDataRefreshAtom, {
          ...lastRefreshTimes,
          tasks: Date.now()
        });
      } catch (err) {
        logger.error('Failed to fetch tasks', err instanceof Error ? err : new Error(String(err)), { atom: 'fetchTasksAtom' });
        set(tasksErrorAtom, err instanceof Error ? err.message : '未知错误');
      } finally {
        set(tasksLoadingAtom, false);
      }
    }
  }
);

// 搜索任务的函数
export const searchTasksAtom = atom(
  null,
  async (get, set, params: TaskSearchParams) => {
    try {
      set(tasksLoadingAtom, true);
      set(tasksErrorAtom, null);
      const data = await TaskService.searchTasks(params);
      set(tasksAtom, data);
    } catch (err) {
      logger.error('Failed to search tasks', err instanceof Error ? err : new Error(String(err)), { atom: 'searchTasksAtom', params });
      set(tasksErrorAtom, err instanceof Error ? err.message : '未知错误');
    } finally {
      set(tasksLoadingAtom, false);
    }
  }
);

// 创建任务 - Optimistic Update (Client-Generated ID)
export const createTaskAtom = atom(
  null,
  async (get, set, taskData: CreateTaskData) => {
    // --- Translation Check ---
    if (get(translationEnabledAtom)) {
      logger.warn(i18n.t('v0/common:errors.editingDisabledWhileTranslating'), { atom: 'createTaskAtom', check: 'translationEnabled' });
      return false;
    }
    // --- End Translation Check ---

    const newTaskId = uuidv4(); // Generate final ID on the client
    const now = new Date().toISOString();

    // Prepare the data to be sent to the backend, including the new ID
    const dataForBackend: CreateTaskData & { id: string } = {
      ...taskData,
      id: newTaskId,
    };

    // Optimistic UI update: Add task with the *final* ID immediately
    const optimisticTaskBase = {
      // Ensure all fields from Task type are present
      title: taskData.title || '新任务', // 确保title始终有值，防止出现undefined
      notes: taskData.notes ?? '',
      done: taskData.done ?? false,
      vital: taskData.vital ?? false,
      urgent: taskData.urgent ?? false,
      fixed: taskData.fixed ?? false,
      dueAt: taskData.dueAt,
      startAt: taskData.startAt,
      cost: taskData.cost,
      reminders: taskData.reminders ?? [],
      // --- System Fields ---
      id: newTaskId,
      createdAt: now,
      updatedAt: now,
      userId: 'optimistic-user', // Placeholder - backend MUST override this with authenticated user
      embedding: null,
    };

    // Conditionally add projectId based on taskData, satisfying Task type
    const optimisticTask: Task = taskData.projectId
      ? { ...optimisticTaskBase, projectId: taskData.projectId }
      : (optimisticTaskBase as Omit<Task, 'projectId'> & { projectId?: string }) as Task;
      // This assumes backend handles missing projectId or Task type allows optional
      // If Task type *strictly* requires projectId, you'd need a client-side default ID

    const previousTasks = get(tasksAtom);
    set(tasksAtom, [optimisticTask, ...previousTasks]);
    set(tasksErrorAtom, null);

    try {
      // Send request to the backend, including the generated ID
      const createdTask: Task = await TaskService.createTask(dataForBackend);

      // ----> ADDED LOG <----
      logger.info(`TaskAtom [createTaskAtom]: Received task from TaskService.createTask`, {
        component: 'createTaskAtom',
        atom: 'createTaskAtom', // Keep atom field for consistency if used elsewhere
        taskId: newTaskId, // Correlate with optimistic step
        returnedTask: createdTask, // Log the entire returned object
        titleSentToBackend: dataForBackend.title // Log the title we sent for comparison
      });
      // ----> END OF ADDED LOG <----

      // Success: Backend should return the task with the same ID.
      // Optionally update the local state with the full confirmed data from backend.
      set(tasksAtom, (currentTasks) =>
        currentTasks.map((task) =>
          task.id === newTaskId ? createdTask : task // Replace placeholder data if needed
        )
      );
      logger.info("Optimistic Create successful, task confirmed by backend", { taskId: newTaskId, atom: 'createTaskAtom' });
      return true;
    } catch (err) {
      logger.error('Optimistic Create Task Failed (Client ID)', err instanceof Error ? err : new Error(String(err)), { atom: 'createTaskAtom', taskId: newTaskId });
      const errorMsg = err instanceof Error ? err.message : '创建任务失败';
      set(tasksErrorAtom, errorMsg);

      // Rollback: Remove the task with the generated ID
      set(tasksAtom, previousTasks.filter(task => task.id !== newTaskId));

      // Clean up any focus state that might have been added for this failed task
      set(basePlayingTaskIdsAtom, (prev) => prev.filter(id => id !== newTaskId));
      set(ephemeralTaskSessionsAtom, (prev) => {
        const { [newTaskId]: _, ...rest } = prev;
        return rest;
      });
      set(userSetTaskProgressAtom, (prev) => {
        const { [newTaskId]: _, ...rest } = prev;
        return rest;
      });

      // Clear focused task if it was this failed task
      const currentFocusedTask = get(focusedTaskIdAtom);
      if (currentFocusedTask === newTaskId) {
        set(focusedTaskIdAtom, null);
      }

      // Update header display if it was showing this failed task
      const currentHeaderDisplay = get(headerActiveDisplayAtom);
      if (currentHeaderDisplay === newTaskId) {
        const remainingPlayingTasks = get(basePlayingTaskIdsAtom).filter(id => id !== newTaskId);
        set(headerActiveDisplayAtom, remainingPlayingTasks.length > 0 ? remainingPlayingTasks[0] : 'clock');
      }

      // Toast handled by caller component
      return false;
    }
  }
);

// 更新任务 - Optimistic Update
export const updateTaskAtom = atom(
  null,
  async (get, set, taskData: UpdateTaskData) => {
    // --- Translation Check ---
    if (get(translationEnabledAtom)) {
      logger.warn(i18n.t('v0/common:errors.editingDisabledWhileTranslating'), { atom: 'updateTaskAtom', check: 'translationEnabled' });
      return false;
    }
    // --- End Translation Check ---

    const taskId = taskData.id;
    if (!taskId) return false;
    const previousTasks = get(tasksAtom);
    const taskIndex = previousTasks.findIndex(t => t.id === taskId);
    if (taskIndex === -1) return false;
    const originalTask = previousTasks[taskIndex];

    const optimisticTask = { ...originalTask, ...taskData, updatedAt: new Date().toISOString() };
    const optimisticTasks = [...previousTasks];
    optimisticTasks[taskIndex] = optimisticTask;

    set(tasksAtom, optimisticTasks);
    set(tasksErrorAtom, null);

    try {
      const updatedTask = await TaskService.updateTask(taskData);
      set(tasksAtom, (currentTasks) =>
        currentTasks.map((task) =>
          task.id === taskId ? updatedTask : task
        )
      );

      // If task was marked as completed, clean up related focus state
      if (updatedTask.done) {
        // Remove from playing tasks
        set(basePlayingTaskIdsAtom, (prev) => prev.filter(id => id !== taskId));

        // Clean up ephemeral sessions
        set(ephemeralTaskSessionsAtom, (prev) => {
          const { [taskId]: _, ...rest } = prev;
          return rest;
        });

        // Clean up user progress
        set(userSetTaskProgressAtom, (prev) => {
          const { [taskId]: _, ...rest } = prev;
          return rest;
        });

        // Clear focused task if it was this task
        const currentFocusedTask = get(focusedTaskIdAtom);
        if (currentFocusedTask === taskId) {
          set(focusedTaskIdAtom, null);
        }

        // Update header display if it was showing this task
        const currentHeaderDisplay = get(headerActiveDisplayAtom);
        if (currentHeaderDisplay === taskId) {
          const remainingPlayingTasks = get(basePlayingTaskIdsAtom).filter(id => id !== taskId);
          set(headerActiveDisplayAtom, remainingPlayingTasks.length > 0 ? remainingPlayingTasks[0] : 'clock');
        }

        // 如果是循环任务完成，延迟刷新任务列表以显示新生成的任务
        if (updatedTask.recurrenceRule && !originalTask.done) {
          logger.info('循环任务完成，将刷新任务列表以显示新生成的任务', { taskId, recurrenceRule: updatedTask.recurrenceRule });

          // 延迟1秒刷新，给后端时间生成新任务
          setTimeout(async () => {
            try {
              const refreshedTasks = await TaskService.getTasks();
              set(tasksAtom, refreshedTasks);
              logger.info('循环任务完成后刷新任务列表成功', { taskId, newTaskCount: refreshedTasks.length });
            } catch (refreshError) {
              logger.error('循环任务完成后刷新任务列表失败', refreshError instanceof Error ? refreshError : new Error(String(refreshError)), { taskId });
            }
          }, 1000);
        }
      }

      logger.info('Optimistic Update successful, task confirmed by backend', { taskId, atom: 'updateTaskAtom' });
      return true;
    } catch (err) {
      logger.error('Optimistic Update Task Failed', err instanceof Error ? err : new Error(String(err)), { taskId, atom: 'updateTaskAtom' });
      const errorMsg = err instanceof Error ? err.message : '更新任务失败';
      set(tasksErrorAtom, errorMsg);
      set(tasksAtom, previousTasks);
      // Removed toast call - component will handle it
      return false;
    }
  }
);

// 删除任务 - Optimistic Update
export const deleteTaskAtom = atom(
  null,
  async (get, set, id: string) => {
    // --- Translation Check ---
    if (get(translationEnabledAtom)) {
      logger.warn(i18n.t('v0/common:errors.editingDisabledWhileTranslating'), { atom: 'deleteTaskAtom', check: 'translationEnabled' });
      return false;
    }
    // --- End Translation Check ---

    if (!id) return false;
    const previousTasks = get(tasksAtom);
    const taskExists = previousTasks.some(task => task.id === id);
    if (!taskExists) return false;

    const optimisticTasks = previousTasks.filter(task => task.id !== id);
    set(tasksAtom, optimisticTasks);
    set(tasksErrorAtom, null);
    logger.info('Optimistic Delete - Removed task locally', { taskId: id, atom: 'deleteTaskAtom' });

    try {
      await TaskService.deleteTask(id);
      logger.info('Optimistic Delete - Confirmed task deleted on backend', { taskId: id, atom: 'deleteTaskAtom' });
      return true;
    } catch (err) {
      logger.error('Optimistic Delete Task Failed', err instanceof Error ? err : new Error(String(err)), { taskId: id, atom: 'deleteTaskAtom' });
      const errorMsg = err instanceof Error ? err.message : '删除任务失败';
      set(tasksErrorAtom, errorMsg);
      set(tasksAtom, previousTasks);
      // Removed toast call - component will handle it
      return false;
    }
  }
);

// 强制刷新任务数据（忽略缓存）
export const forceRefreshTasksAtom = atom(
  null,
  async (get, set) => {
    const lastRefreshTimes = get(lastDataRefreshAtom);

    set(tasksLoadingAtom, true);
    set(tasksErrorAtom, null);
    try {
      const data = await TaskService.getTasks();
      set(tasksAtom, data);
      // 更新最后刷新时间
      set(lastDataRefreshAtom, {
        ...lastRefreshTimes,
        tasks: Date.now()
      });
      logger.info('Force refresh tasks successful', { atom: 'forceRefreshTasksAtom' });
    } catch (err) {
      logger.error('Failed to force refresh tasks', err instanceof Error ? err : new Error(String(err)), { atom: 'forceRefreshTasksAtom' });
      set(tasksErrorAtom, err instanceof Error ? err.message : '未知错误');
    } finally {
      set(tasksLoadingAtom, false);
    }
  }
);